import React, { useState, useEffect } from 'react';
import { Sparkles, Key, CheckCircle2, AlertTriangle, ShieldCheck, Loader2, Save, RefreshCw, Cpu } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AiSettings() {
  const [provider, setProvider] = useState('openai');
  const [apiKey, setApiKey] = useState('');
  const [modelName, setModelName] = useState('');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/api/ai/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        setProvider(data.provider || 'gemini');
        setModelName(data.model_name || '');
      }
    } catch (e) {
      console.error('Failed to load AI config:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/api/ai/config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          provider,
          api_key: apiKey,
          model_name: modelName
        })
      });
      if (res.ok) {
        setMessage({ type: 'success', text: 'AI Configuration saved successfully!' });
        setApiKey('');
        fetchConfig();
      } else {
        const err = await res.json();
        setMessage({ type: 'error', text: err.detail || 'Failed to save configuration' });
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Network error saving configuration' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/api/ai/copilot-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          message: 'Hello LOGI AI Copilot! Confirm your connection and status in 1 short sentence.'
        })
      });
      if (res.ok) {
        const data = await res.json();
        setTestResult({ success: true, reply: data.reply });
      } else {
        setTestResult({ success: false, error: 'AI test request failed' });
      }
    } catch (e) {
      setTestResult({ success: false, error: 'Network error communicating with AI endpoint' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-purple-600 to-indigo-600 flex items-center justify-center text-white shadow-md">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800 font-manrope">AI Engine & Job Copilot Settings</h2>
            <p className="text-xs text-gray-500 font-ibm">
              Configure Google Gemini or OpenAI to automate error audits, document parsing, and real-time chat copilot.
            </p>
          </div>
        </div>

        {config?.has_api_key ? (
          <div className="flex flex-col items-end gap-1">
            <span className="flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-bold">
              <CheckCircle2 className="w-4 h-4" />
              AI Active ({config.masked_key})
            </span>
            <span className="text-[10px] text-emerald-600 font-semibold flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" />
              AES Encrypted in DB
            </span>
          </div>
        ) : (
          <span className="flex items-center gap-1.5 px-3 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-xs font-bold">
            <AlertTriangle className="w-4 h-4" />
            Using Built-in Logistics Engine (Offline Fallback)
          </span>
        )}
      </div>

      {message && (
        <div
          className={`p-3 rounded-xl text-xs font-semibold flex items-center gap-2 ${
            message.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-rose-50 text-rose-800 border border-rose-200'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSave} className="space-y-5">
        {/* Provider Selection */}
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
            Select AI Provider
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label
              className={`p-4 rounded-xl border-2 flex items-center gap-3 cursor-pointer transition-all ${
                provider === 'gemini'
                  ? 'border-blue-600 bg-blue-50/50 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="provider"
                value="gemini"
                checked={provider === 'gemini'}
                onChange={() => setProvider('gemini')}
                className="hidden"
              />
              <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-xs">
                G
              </div>
              <div>
                <p className="font-bold text-sm text-slate-800">Google Gemini</p>
                <p className="text-[11px] text-gray-500">Gemini 1.5 Flash / Pro (Fast, High token context)</p>
              </div>
            </label>

            <label
              className={`p-4 rounded-xl border-2 flex items-center gap-3 cursor-pointer transition-all ${
                provider === 'openai'
                  ? 'border-emerald-600 bg-emerald-50/50 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="provider"
                value="openai"
                checked={provider === 'openai'}
                onChange={() => setProvider('openai')}
                className="hidden"
              />
              <div className="w-8 h-8 rounded-lg bg-emerald-600 text-white flex items-center justify-center font-bold text-xs">
                OA
              </div>
              <div>
                <p className="font-bold text-sm text-slate-800">OpenAI</p>
                <p className="text-[11px] text-gray-500">GPT-4o / GPT-4o-mini (Leading reasoning & extraction)</p>
              </div>
            </label>
          </div>
        </div>

        {/* API Key */}
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">
            API Key
          </label>
          <div className="relative">
            <Key className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={config?.has_api_key ? '•••••••••••••••••••••••• (Leave blank to keep current key)' : 'Paste your API key here (AIzaSy... or sk-...)'}
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>
          <p className="text-[11px] text-gray-400 mt-1">
            🔒 Keys are encrypted with AES-128-CBC + HMAC-SHA256 before persisting in MySQL. Never stored in plain text.
          </p>
        </div>

        {/* Model Name */}
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">
            Model Name (Optional)
          </label>
          <div className="relative">
            <Cpu className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={modelName}
              onChange={(e) => setModelName(e.target.value)}
              placeholder={provider === 'gemini' ? 'gemini-1.5-flash (Default)' : 'gpt-4o-mini (Default)'}
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Buttons */}
        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-sm font-bold shadow-sm transition-all"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <span>Save Configuration</span>
          </button>

          <button
            type="button"
            onClick={handleTestConnection}
            disabled={testing}
            className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-slate-700 rounded-xl text-sm font-bold transition-all"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin text-blue-600" /> : <RefreshCw className="w-4 h-4" />}
            <span>Test Connection</span>
          </button>
        </div>
      </form>

      {/* Test Connection Output */}
      {testResult && (
        <div className={`p-4 rounded-xl border ${testResult.success ? 'bg-emerald-50/60 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
          <p className="text-xs font-bold text-slate-800 mb-1">
            {testResult.success ? '✅ AI Connection Verified:' : '❌ Test Failed:'}
          </p>
          <p className="text-xs text-slate-700 leading-relaxed font-mono">
            {testResult.reply || testResult.error}
          </p>
        </div>
      )}

      {/* Built-in AI Capabilities Guide */}
      <div className="pt-4 border-t border-gray-100">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-3">
          Automated AI Features Across the Job Lifetime
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="p-3 bg-slate-50 rounded-xl border border-gray-200">
            <span className="font-bold text-xs text-blue-700">1. Real-Time Chat Copilot</span>
            <p className="text-[11px] text-gray-500 mt-1">
              Type <code className="bg-white px-1 rounded text-slate-800">@ai</code> in any channel or docket thread to ask questions, check requirements, or summarize progress.
            </p>
          </div>
          <div className="p-3 bg-slate-50 rounded-xl border border-gray-200">
            <span className="font-bold text-xs text-emerald-700">2. Proactive Error Audit</span>
            <p className="text-[11px] text-gray-500 mt-1">
              Scans weight/CBM ratios, Dangerous Goods classifications, and cold-chain logger requirements to prevent compliance failures.
            </p>
          </div>
          <div className="p-3 bg-slate-50 rounded-xl border border-gray-200">
            <span className="font-bold text-xs text-purple-700">3. Automated Client Drafting</span>
            <p className="text-[11px] text-gray-500 mt-1">
              Generates tailored WhatsApp and Email milestone updates with one click, eliminating repetitive typing.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
